/**
 * 🛡️ EquipmentPage — phoenix-anatomy loadout + owned-gear browser.
 *
 * Shows 5 phoenix-anatomy slot cards (helmet · chest · wings · talons · aura)
 * + a flat-stat panel + active set bonuses. Below that, the owned gear is
 * grouped by set so the player can equip / unequip / see what's missing.
 *
 * Owner-only "Grant Set" buttons let the owner drop a full 5-piece set
 * into inventory for testing without grinding the Blacksmith.
 *
 * Endpoints used:
 *   GET  /api/equipment/loadout
 *   GET  /api/equipment/catalog
 *   POST /api/equipment/equip      { item_id }
 *   POST /api/equipment/unequip    { slot }
 *   POST /api/equipment/admin/grant-set  { set_id }   (owner-only)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PyrotechnicPhoenixPanel from "@/components/PyrotechnicPhoenixPanel";
import RainbowCanonPanel from "@/components/RainbowCanonPanel";
import AuraUpgradePanel from "@/components/AuraUpgradePanel";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The 5 phoenix anatomy slots — display order top → bottom of body.
const PHOENIX_SLOTS = [
  { id: "helmet", icon: "🪶", label: "Helmet" },
  { id: "chest",  icon: "🛡️", label: "Chest"  },
  { id: "wings",  icon: "🪽", label: "Wings"  },
  { id: "talons", icon: "🦅", label: "Talons" },
  { id: "aura",   icon: "✨", label: "Aura"   },
];

// Friendly labels + sort order for stat keys (used everywhere a stat appears)
const STAT_DEFS = {
  hp:               { label: "Max HP",         order: 1,  unit: ""   },
  atk:              { label: "Attack",         order: 2,  unit: ""   },
  patk:             { label: "Phys Attack",    order: 3,  unit: ""   },
  mag_pwr:          { label: "Magic Power",    order: 4,  unit: ""   },
  matk:             { label: "Mag Attack",     order: 5,  unit: ""   },
  def_:             { label: "Defense",        order: 6,  unit: ""   },
  pdef:             { label: "Phys Defense",   order: 7,  unit: ""   },
  mdef:             { label: "Mag Defense",    order: 8,  unit: ""   },
  max_mp:           { label: "Max MP",         order: 9,  unit: ""   },
  spd:              { label: "Speed",          order: 10, unit: ""   },
  crit:             { label: "Crit",           order: 11, unit: ""   },
  spell_crit_pct:   { label: "Spell Crit",     order: 12, unit: "%"  },
  armor_pen:        { label: "Armor Pen",      order: 13, unit: ""   },
  acc_pct:          { label: "Accuracy",       order: 14, unit: "%"  },
  evasion_pct:      { label: "Evasion",        order: 15, unit: "%"  },
  block_pct:        { label: "Block",          order: 16, unit: "%"  },
  debuff_resist_pct:{ label: "Debuff Resist",  order: 17, unit: "%"  },
  phys_dmg_pct:     { label: "Phys Damage",    order: 18, unit: "%"  },
  counter_dmg:      { label: "Counter Dmg",    order: 19, unit: ""   },
  counter_dmg_pct:  { label: "Counter Dmg",    order: 20, unit: "%"  },
};

function formatStat(key, value) {
  const def = STAT_DEFS[key] || { label: key, order: 99, unit: "" };
  const sign = value > 0 ? "+" : "";
  return { label: def.label, text: `${sign}${value}${def.unit}`, value };
}

function statsToList(stats) {
  if (!stats) return [];
  return Object.entries(stats)
    .filter(([, v]) => typeof v === "number" && v !== 0)
    .map(([k, v]) => ({ key: k, ...formatStat(k, v) }))
    .sort((a, b) => (STAT_DEFS[a.key]?.order || 99) - (STAT_DEFS[b.key]?.order || 99));
}

/* ─────────────────────────────────────────────────────────────── */

export default function EquipmentPage() {
  const { user, loading: authLoading } = useAuth();
  const [loadout, setLoadout] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filterSetId, setFilterSetId] = useState("all");

  const refresh = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([
        axios.get(`${API}/equipment/loadout`),
        axios.get(`${API}/equipment/catalog`),
      ]);
      setLoadout(a.data);
      setCatalog(b.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load equipment.");
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    refresh();
  }, [authLoading, user, refresh]);

  const equip = async (item_id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/equipment/equip`, { item_id });
      toast.success("Equipped");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Equip failed.");
    }
    setBusy(false);
  };

  const unequip = async (slot) => {
    setBusy(true);
    try {
      await axios.post(`${API}/equipment/unequip`, { slot });
      toast.success("Unequipped");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unequip failed.");
    }
    setBusy(false);
  };

  const grantSet = async (set_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/equipment/admin/grant-set`, { set_id });
      toast.success(`Granted ${r.data.granted.length} pieces`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed.");
    }
    setBusy(false);
  };

  if (authLoading) return null;
  if (!user) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-400" data-testid="equipment-locked">
          🔒 Sign in to view your equipment.
        </div>
      </Shell>
    );
  }
  if (!loadout || !catalog) {
    return (
      <Shell>
        <div className="max-w-3xl mx-auto p-8 text-center text-neutral-500" data-testid="equipment-loading">
          Polishing your gear…
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="max-w-6xl mx-auto px-4 py-6 pb-24" data-testid="equipment-page">
        <HeroBanner catalog={catalog} loadout={loadout} />
        <SlotGrid loadout={loadout} onUnequip={unequip} busy={busy} />
        <StatsPanel bonuses={loadout.bonuses} />
        <ActiveSetsPanel sets={loadout.bonuses?.sets || []} />
        <AuraUpgradePanel />
        <PyrotechnicPhoenixPanel isOwner={!!catalog.is_owner} />
        <RainbowCanonPanel />
        <CatalogBrowser
          catalog={catalog}
          loadout={loadout}
          filterSetId={filterSetId}
          setFilterSetId={setFilterSetId}
          onEquip={equip}
          onGrantSet={grantSet}
          busy={busy}
        />
      </div>
    </Shell>
  );
}

/* ─────────────────────────────────────────────────────────────── */

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-neutral-200">
      <Header />
      {children}
      <Footer />
    </div>
  );
}

function HeroBanner({ catalog, loadout }) {
  const equippedCount = Object.values(loadout?.slots || {}).filter(Boolean).length;
  return (
    <div
      className="mb-6 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a1305] via-[#0A0A0A] to-[#1a0a05] p-5 shadow-[0_0_30px_rgba(245,158,11,0.18)]"
      data-testid="equipment-hero-banner"
    >
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div
            className="text-3xl font-black tracking-wide text-amber-300"
            style={{ fontFamily: "Anton, sans-serif" }}
          >
            🛡️ THE ARMORY
          </div>
          <div className="text-xs text-neutral-400 mt-1 leading-relaxed max-w-xl">
            Equip the phoenix anatomy — <span className="text-amber-300">helmet · chest · wings · talons · aura</span>.
            Five matching pieces unlock the set's full resonance.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Pill icon="🦅" label="Class" value={catalog.user_class || "—"} testid="equip-pill-class" />
          <Pill icon="🎒" label="Equipped" value={`${equippedCount}/5`} testid="equip-pill-count" />
          {catalog.is_owner && <Pill icon="👑" label="Owner" value="dev" testid="equip-pill-owner" />}
          {/* 🖼️ Oct 2026 — doorway to the Dragonious hero-preview stage.
              Lets the player bounce from the data-driven armory to the
              beautiful empty-canvas and back. */}
          <a
            href="/armory/preview"
            data-testid="equip-hero-preview-link"
            className="px-3 py-1.5 rounded-full bg-gradient-to-br from-amber-700/50 via-black/40 to-rose-800/50 border border-amber-400/60 text-sm flex items-center gap-1.5 text-amber-100 hover:border-amber-200/80 transition-colors"
          >
            🖼️ Hero Preview
          </a>
        </div>
      </div>
    </div>
  );
}

function Pill({ icon, label, value, testid }) {
  return (
    <div
      className="px-3 py-1.5 rounded-full bg-black/40 border border-neutral-700 text-sm flex items-center gap-1.5"
      data-testid={testid}
    >
      <span>{icon}</span>
      <span className="font-bold text-amber-300">{value}</span>
      <span className="text-neutral-500 text-xs">{label}</span>
    </div>
  );
}

/* ─────────── 5 phoenix-anatomy slot cards ─────────── */

function SlotGrid({ loadout, onUnequip, busy }) {
  const slots = loadout?.slots || {};
  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6"
      data-testid="equipment-slot-grid"
    >
      {PHOENIX_SLOTS.map(({ id, icon, label }) => (
        <SlotCard
          key={id}
          slotId={id}
          icon={icon}
          label={label}
          piece={slots[id]}
          onUnequip={onUnequip}
          busy={busy}
        />
      ))}
    </div>
  );
}

function SlotCard({ slotId, icon, label, piece, onUnequip, busy }) {
  if (!piece) {
    return (
      <div
        className="rounded-xl border-2 border-dashed border-neutral-800 bg-neutral-950/60 p-3 min-h-[140px] flex flex-col items-center justify-center text-center"
        data-testid={`equipment-slot-${slotId}-empty`}
      >
        <div className="text-3xl opacity-40 mb-1">{icon}</div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-600">{label}</div>
        <div className="text-[10px] text-neutral-700 mt-1">Empty</div>
      </div>
    );
  }
  const pieceStats = statsToList(piece.stats);
  return (
    <div
      className="rounded-xl border border-amber-600/40 bg-gradient-to-br from-amber-950/30 to-neutral-950 p-3 min-h-[140px] flex flex-col"
      data-testid={`equipment-slot-${slotId}-filled`}
    >
      <div className="flex items-start justify-between gap-1 mb-1">
        <span className="text-2xl">{piece.icon || icon}</span>
        <button
          onClick={() => onUnequip(slotId)}
          disabled={busy}
          data-testid={`equipment-slot-${slotId}-unequip`}
          className="text-[9px] uppercase tracking-widest text-neutral-500 hover:text-red-300 disabled:opacity-50"
        >
          ✕ Off
        </button>
      </div>
      <div className="text-xs font-bold text-amber-200 leading-tight line-clamp-2">{piece.name}</div>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-1">{label}</div>
      <div className="flex flex-wrap gap-1 mt-2">
        {pieceStats.map((s) => (
          <span
            key={s.key}
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              s.value > 0 ? "bg-emerald-950/60 text-emerald-300" : "bg-red-950/60 text-red-300"
            }`}
            data-testid={`equipment-slot-${slotId}-stat-${s.key}`}
          >
            {s.text} {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ─────────── flat stats panel ─────────── */

function StatsPanel({ bonuses }) {
  if (!bonuses) return null;
  const list = statsToList(bonuses.bonus);
  if (!list.length) {
    return (
      <div
        className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 mb-6 text-center text-xs text-neutral-500"
        data-testid="equipment-stats-empty"
      >
        Equip gear to see your combat bonuses here.
      </div>
    );
  }
  return (
    <div
      className="rounded-xl border border-neutral-800 bg-neutral-950 p-4 mb-6"
      data-testid="equipment-stats-panel"
    >
      <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-3">
        ✨ Total Combat Bonus
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {list.map((s) => (
          <div
            key={s.key}
            className="flex justify-between items-baseline border-b border-neutral-900 pb-1"
            data-testid={`equipment-bonus-${s.key}`}
          >
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">{s.label}</span>
            <span className={`text-sm font-bold ${s.value > 0 ? "text-emerald-300" : "text-red-300"}`}>
              {s.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────── active set perks ─────────── */

function ActiveSetsPanel({ sets }) {
  if (!sets || !sets.length) return null;
  return (
    <div className="mb-6 space-y-2" data-testid="equipment-active-sets">
      {sets.map((s) => (
        <div
          key={s.set_id}
          className="rounded-xl border border-emerald-700/40 bg-gradient-to-r from-emerald-950/30 to-neutral-950 p-3"
          data-testid={`equipment-active-set-${s.set_id}`}
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-bold text-emerald-300">
              ✨ {s.name} <span className="text-neutral-500 font-normal text-xs">· {s.pieces_equipped}/5 equipped</span>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            {s.perks.map((p, i) => (
              <div
                key={i}
                className={`text-xs ${p.pair_unmet ? "text-amber-400" : "text-emerald-200"}`}
                data-testid={`equipment-perk-${s.set_id}-${p.pieces}`}
              >
                <span className="font-bold">{p.pieces}/5 · {p.name}</span>
                <span className="text-neutral-400"> — {p.description}</span>
                {p.pair_required && (
                  <div className="text-[10px] mt-0.5 text-amber-400">
                    {p.pair_unmet
                      ? `⚠ Both ${(p.required_heroes || []).join(" + ")} must be in your active party.`
                      : `🤝 Pair active — ${(p.required_heroes || []).join(" + ")} in party.`}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─────────── owned + browseable catalog ─────────── */

function CatalogBrowser({ catalog, loadout, filterSetId, setFilterSetId, onEquip, onGrantSet, busy }) {
  // Group items by set_id; preserve display order: owned-first, then unowned.
  const grouped = useMemo(() => {
    const m = new Map();
    for (const it of catalog.items || []) {
      const sid = it.set_id || "_unsorted";
      if (!m.has(sid)) m.set(sid, []);
      m.get(sid).push(it);
    }
    return m;
  }, [catalog.items]);

  // Build a list of {set_id, name, items[], owned_count, has_any_owned}
  const setRows = useMemo(() => {
    const knownSets = new Map((catalog.sets || []).map((s) => [s.set_id, s]));
    const rows = [];
    for (const [sid, items] of grouped) {
      const meta = knownSets.get(sid);
      const ownedCount = items.filter((i) => i.owned > 0).length;
      rows.push({
        set_id: sid,
        name: meta?.name || (sid === "_unsorted" ? "Misc." : sid),
        items,
        ownedCount,
        totalCount: items.length,
        themeMessage: meta?.set_theme_message,
        pairRequired: !!meta?.pair_required,
        requiredHeroes: meta?.required_heroes,
      });
    }
    // Sort: rows with owned items first, then alphabetical
    rows.sort((a, b) => {
      if ((b.ownedCount > 0) !== (a.ownedCount > 0)) return (b.ownedCount > 0) - (a.ownedCount > 0);
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [grouped, catalog.sets]);

  const visibleRows = filterSetId === "all" ? setRows : setRows.filter((r) => r.set_id === filterSetId);

  return (
    <div data-testid="equipment-catalog-browser">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="text-[10px] uppercase tracking-widest text-amber-300">
          🎒 Available Gear
        </div>
        <select
          value={filterSetId}
          onChange={(e) => setFilterSetId(e.target.value)}
          className="bg-neutral-950 border border-neutral-800 text-xs text-neutral-300 px-2 py-1 rounded"
          data-testid="equipment-set-filter"
        >
          <option value="all">All Sets ({setRows.length})</option>
          {setRows.map((r) => (
            <option key={r.set_id} value={r.set_id}>
              {r.name} ({r.ownedCount}/{r.totalCount} owned)
            </option>
          ))}
        </select>
      </div>

      {visibleRows.length === 0 && (
        <div className="text-center text-neutral-500 text-sm py-8" data-testid="equipment-catalog-empty">
          No gear available for your class yet. Forge some at the ⚒️ Blacksmith.
        </div>
      )}

      <div className="space-y-4">
        {visibleRows.map((r) => (
          <SetRow
            key={r.set_id}
            row={r}
            onEquip={onEquip}
            onGrantSet={onGrantSet}
            isOwner={catalog.is_owner}
            equippedSlots={loadout?.slots || {}}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}

function SetRow({ row, onEquip, onGrantSet, isOwner, equippedSlots, busy }) {
  return (
    <div
      className="rounded-xl border border-neutral-800 bg-neutral-950/50 p-3"
      data-testid={`equipment-set-row-${row.set_id}`}
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-sm font-bold text-amber-200">
            {row.name}
            <span className="text-neutral-500 font-normal text-xs">
              {" · "}{row.ownedCount}/{row.totalCount} owned
            </span>
          </div>
          {row.themeMessage && (
            <div className="text-[10px] italic text-neutral-500 mt-0.5 max-w-xl leading-relaxed">
              "{row.themeMessage}"
            </div>
          )}
          {row.pairRequired && (
            <div className="text-[10px] mt-1 text-amber-400">
              🤝 Pair-required: {(row.requiredHeroes || []).join(" + ")} must be in party.
            </div>
          )}
        </div>
        {isOwner && row.set_id !== "_unsorted" && (
          <button
            onClick={() => onGrantSet(row.set_id)}
            disabled={busy}
            data-testid={`equipment-grant-set-${row.set_id}`}
            className="text-[10px] uppercase tracking-widest px-2 py-1 bg-purple-900/40 hover:bg-purple-800/60 text-purple-200 border border-purple-700/40 rounded disabled:opacity-50"
            title="Owner: drop all 5 pieces into inventory for testing"
          >
            👑 Grant 5/5
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {row.items.map((it) => (
          <ItemCard
            key={it.item_id}
            item={it}
            equippedInSlot={equippedSlots?.[it.slot] || null}
            onEquip={onEquip}
            busy={busy}
          />
        ))}
      </div>
    </div>
  );
}

function ItemCard({ item, equippedInSlot, onEquip, busy }) {
  const owned = item.owned > 0;
  const equipped = !!item.equipped;
  const stats = statsToList(item.stats);
  // 🧮 Sep 2026 (Roc #41) — per-stat comparison vs the item currently in
  // THIS item's slot. ⬆️ upgrade · ⬇️ downgrade · · neutral. Empty slot ⇒
  // every stat reads as a straight upgrade. If this card IS the equipped
  // piece, no arrows.
  const compareStats = (() => {
    if (equipped) return null;                   // same piece — nothing to compare
    const cur = equippedInSlot?.stats || null;
    return stats.map((s) => {
      const curVal = Number((cur || {})[s.key] || 0);
      const newVal = Number(s.value || 0);
      const delta = newVal - curVal;
      let arrow = "·";
      let tone = "text-neutral-500";
      if (!cur) {
        arrow = "⬆";
        tone = "text-emerald-300";
      } else if (delta > 0) {
        arrow = "⬆";
        tone = "text-emerald-300";
      } else if (delta < 0) {
        arrow = "⬇";
        tone = "text-red-300";
      }
      return { ...s, delta, arrow, tone };
    });
  })();
  return (
    <div
      className={`rounded-lg border p-3 ${
        equipped
          ? "border-emerald-600/50 bg-emerald-950/20"
          : owned
            ? "border-amber-700/40 bg-amber-950/10"
            : "border-neutral-900 bg-black/40 opacity-60"
      }`}
      data-testid={`equipment-item-${item.item_id}`}
    >
      <div className="flex items-start gap-2">
        <span className="text-2xl shrink-0">{item.icon || "🎒"}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold text-amber-200 leading-tight">{item.name}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-500 mt-0.5">
            {item.slot} · Lv {item.level_req || 1}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 mt-2">
        {(compareStats || stats).map((s) => {
          const isComparable = !!compareStats;
          return (
            <span
              key={s.key}
              className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-0.5 ${
                s.value > 0 ? "bg-emerald-950/60 text-emerald-300" : "bg-red-950/60 text-red-300"
              }`}
              title={isComparable
                ? `${s.label}: ${s.text}  (vs equipped ${s.delta > 0 ? "+" : ""}${s.delta})`
                : `${s.label}: ${s.text}`}
              data-testid={`gear-stat-${item.item_id}-${s.key}`}
            >
              {s.text} {s.label}
              {isComparable && (
                <span className={`ml-0.5 font-bold ${s.tone}`} data-testid={`gear-stat-arrow-${item.item_id}-${s.key}`}>
                  {s.arrow}
                </span>
              )}
            </span>
          );
        })}
      </div>

      {item.subliminal_message && (
        <div className="text-[10px] italic text-neutral-500 mt-2 leading-snug">
          "{item.subliminal_message}"
        </div>
      )}

      <div className="mt-3">
        {equipped ? (
          <div
            className="text-center text-[10px] uppercase tracking-widest text-emerald-300 py-1.5 border border-emerald-700/40 rounded"
            data-testid={`equipment-item-${item.item_id}-equipped`}
          >
            ✓ Equipped
          </div>
        ) : owned ? (
          <button
            onClick={() => onEquip(item.item_id)}
            disabled={busy}
            data-testid={`equipment-item-${item.item_id}-equip-btn`}
            className="w-full text-[10px] uppercase tracking-widest font-bold py-1.5 bg-amber-700 hover:bg-amber-600 text-white rounded disabled:opacity-50 transition"
          >
            Equip
          </button>
        ) : (
          <div
            className="text-center text-[10px] uppercase tracking-widest text-neutral-600 py-1.5 border border-neutral-900 rounded"
            data-testid={`equipment-item-${item.item_id}-locked`}
          >
            Not yet forged
          </div>
        )}
      </div>
    </div>
  );
}
